import React, { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { getOrderById } from "../services/api";
import Card from "../components/Card";
import {
  formatCurrency,
  formatDateInEasternDT,
  formatTimeInEasternTI
} from "../utils/formatters";
import {
  FaArrowLeft,
  FaPrint,
  FaEnvelope,
  FaDownload,
  FaSpinner
} from "react-icons/fa";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import logo from "../assets/liferx-logo.png";

// Styled components
const PageWrapper = styled.div`
  padding: 24px;
  background-color: var(--bg-primary, #1a1a1a);
  min-height: 100vh;
  width: 100%;
`;

const ReceiptContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  max-width: 800px;
  margin: 0 auto;
`;

const ReceiptHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ReceiptTitle = styled.h2`
  font-size: 1.75rem;
  font-weight: 600;
  color: var(--text-primary, #f0f0f0);
  margin: 0;
`;

const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  color: var(--primary, #6366f1);
  font-weight: 500;
  transition: all 0.2s;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;

  &:hover {
    color: var(--primary-dark, #4f46e5);
    background-color: var(--hover-color, #2c2c2c);
    text-decoration: none;
  }

  svg {
    margin-right: 0.5rem;
  }
`;

const ReceiptContent = styled.div`
  background-color: var(--card-bg, #2a2a2a);
  border-radius: 8px;
  overflow: hidden;
  padding: 2rem;
`;

const ReceiptLogoSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  border-bottom: 1px solid var(--border-color, #3a3a3a);
  padding-bottom: 1.5rem;
`;

const CompanyInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

// Logo styled component
const LogoImage = styled.img`
  max-height: 60px;
  width: auto;
  margin-bottom: 0.5rem;
`;

const CompanyDetails = styled.div`
  color: var(--text-secondary, #aaa);
  font-size: 0.9rem;
  line-height: 1.4;
`;

const ReceiptInfo = styled.div`
  text-align: right;
`;

const ReceiptLabel = styled.h4`
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary, #f0f0f0);
  margin: 0 0 0.5rem 0;
`;

const ReceiptNumber = styled.div`
  color: var(--primary, #6366f1);
  font-weight: 500;
  font-size: 1rem;
  margin-bottom: 0.25rem;
`;

const ReceiptDate = styled.div`
  color: var(--text-secondary, #aaa);
  font-size: 0.9rem;
`;

const ReceiptSection = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h4`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary, #f0f0f0);
  margin: 0 0 1rem 0;
  border-bottom: 1px solid var(--border-color, #3a3a3a);
  padding-bottom: 0.5rem;
`;

const AddressGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-bottom: 2rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const AddressBlock = styled.div`
  line-height: 1.5;
`;

const AddressTitle = styled.div`
  font-weight: 600;
  color: var(--text-primary, #f0f0f0);
  margin-bottom: 0.5rem;
`;

const AddressContent = styled.div`
  color: var(--text-secondary, #aaa);
  font-size: 0.9rem;
`;

const CustomerLabel = styled.div`
  font-weight: 600;
  color: var(--text-primary, #f0f0f0);
  margin-bottom: 0.25rem;
  font-size: 0.9rem;
`;

const CustomerValue = styled.div`
  color: var(--text-secondary, #aaa);
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 1.5rem;
`;

const TableHead = styled.thead`
  background-color: var(--bg-secondary, #252525);

  th {
    padding: 0.75rem 1rem;
    text-align: left;
    font-weight: 600;
    color: var(--text-primary, #f0f0f0);
    font-size: 0.9rem;
  }
`;

const TableBody = styled.tbody`
  td {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid var(--border-color, #3a3a3a);
    color: var(--text-secondary, #aaa);
    font-size: 0.9rem;
  }

  tr:last-child td {
    border-bottom: none;
  }
`;

const ItemName = styled.div`
  color: var(--text-primary, #f0f0f0);
  font-weight: 500;
  margin-bottom: 0.25rem;
`;

const ItemDescription = styled.div`
  color: var(--text-secondary, #aaa);
  font-size: 0.85rem;
`;

const TotalSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  border-top: 1px solid var(--border-color, #3a3a3a);
  padding-top: 1.5rem;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  max-width: 300px;
  margin-bottom: 0.5rem;

  &:last-child {
    margin-top: 0.5rem;
    margin-bottom: 0;
    padding-top: 0.5rem;
    border-top: 1px solid var(--border-color, #3a3a3a);
    font-weight: 600;
    font-size: 1.1rem;
    color: var(--text-primary, #f0f0f0);
  }
`;

const TotalLabel = styled.div`
  color: var(--text-secondary, #aaa);
`;

const TotalValue = styled.div`
  color: var(--text-primary, #f0f0f0);
`;

const GrandTotal = styled.div`
  color: var(--primary, #6366f1);
  font-weight: 700;
`;

const Actions = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  justify-content: center;
  flex-wrap: wrap;

  @media (max-width: 640px) {
    flex-direction: column;
  }
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  background-color: ${(props) =>
    props.primary ? "var(--primary, #6366f1)" : "var(--bg-secondary, #252525)"};
  color: ${(props) =>
    props.primary ? "white" : "var(--text-primary, #f0f0f0)"};

  &:hover:not(:disabled) {
    background-color: ${(props) =>
      props.primary
        ? "var(--primary-dark, #4f46e5)"
        : "var(--hover-color, #2c2c2c)"};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  svg {
    font-size: 1.1rem;
  }
`;

const Footer = styled.div`
  text-align: center;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--border-color, #3a3a3a);
  color: var(--text-secondary, #aaa);
  font-size: 0.9rem;
`;

const ThankYou = styled.div`
  font-size: 1.125rem;
  color: var(--text-primary, #f0f0f0);
  margin-bottom: 0.5rem;
  font-weight: 500;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: var(--text-secondary, #aaa);
  font-size: 1.125rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;

  svg {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: var(--error, #ef4444);
  font-size: 1.125rem;
`;

// Create a styled component for the receipt PDF version
const PDFReceiptContainer = styled.div`
  position: absolute;
  top: -9999px;
  left: -9999px;
  width: 210mm; /* A4 width */
  background-color: white;
  color: black;
  padding: 20mm;
  font-family: Arial, sans-serif;
`;

const ReceiptPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const receiptRef = useRef(null);
  const pdfReceiptRef = useRef(null);

  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        const data = await getOrderById(id);
        console.log("Fetched order details:", data);
        setOrder(data);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching order details:", err);
        setError("Error loading order details. Please try again later.");
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [id]);

  const handlePrint = () => {
    setIsPrinting(true);
    setTimeout(() => {
      window.print();
      setIsPrinting(false);
    }, 300);
  };

  const handleSendEmail = () => {
    // Implement email functionality here
    alert("Email functionality will be implemented later.");
  };

  const handleDownloadPDF = async () => {
    setIsGeneratingPDF(true);

    try {
      // Wait for a moment to ensure the receipt content is rendered
      setTimeout(async () => {
        if (pdfReceiptRef.current) {
          // First, change background and text colors for PDF generation
          const originalElements = pdfReceiptRef.current.querySelectorAll("*");
          originalElements.forEach((el) => {
            el.style.color = "black";
            el.style.backgroundColor = "white";
          });

          const canvas = await html2canvas(pdfReceiptRef.current, {
            scale: 2, // Higher scale for better quality
            useCORS: true,
            logging: false,
            backgroundColor: "#ffffff"
          });

          const imgData = canvas.toDataURL("image/png");

          // Create PDF with A4 dimensions
          const pdf = new jsPDF({
            orientation: "portrait",
            unit: "mm",
            format: "a4"
          });

          const imgWidth = 210; // A4 width in mm (excluding margins)
          const imgHeight = (canvas.height * imgWidth) / canvas.width;

          pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);

          // Download the PDF
          pdf.save(`Order_Receipt_${order.order_id}.pdf`);

          setIsGeneratingPDF(false);
        } else {
          console.error("PDF receipt reference not found");
          setIsGeneratingPDF(false);
        }
      }, 500);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Error generating PDF. Please try again.");
      setIsGeneratingPDF(false);
    }
  };

  if (loading) {
    return (
      <PageWrapper>
        <ReceiptContainer>
          <Card>
            <LoadingMessage>
              <FaSpinner />
              Loading receipt...
            </LoadingMessage>
          </Card>
        </ReceiptContainer>
      </PageWrapper>
    );
  }

  if (error) {
    return (
      <PageWrapper>
        <ReceiptContainer>
          <Card>
            <ErrorMessage>{error}</ErrorMessage>
          </Card>
        </ReceiptContainer>
      </PageWrapper>
    );
  }

  if (!order) {
    return (
      <PageWrapper>
        <ReceiptContainer>
          <Card>
            <ErrorMessage>Order not found.</ErrorMessage>
          </Card>
        </ReceiptContainer>
      </PageWrapper>
    );
  }

  // Calculate subtotal
  const subtotal =
    order.order_items && order.order_items.length > 0
      ? order.order_items.reduce(
          (sum, item) => sum + (parseFloat(item.price) || 0),
          0
        )
      : 0;

  // Format date in Eastern Time using the utility functions
  const formattedDate = formatDateInEasternDT(
    order.order_date,
    order.order_time
  );
  const formattedTime = order.order_time
    ? formatTimeInEasternTI(order.order_date, order.order_time)
    : "N/A";

  // Render the receipt content
  const renderReceiptContent = (isPDF = false) => (
    <ReceiptContent
      style={
        isPDF
          ? {
              backgroundColor: "white",
              color: "black",
              padding: isPDF ? "1rem" : "2rem" // Reduced padding for PDF
            }
          : {}
      }
    >
      <ReceiptLogoSection
        style={isPDF ? { marginBottom: "1rem", paddingBottom: "1rem" } : {}}
      >
        <CompanyInfo>
          <LogoImage
            src={logo}
            alt="Liferx MD Logo"
            style={isPDF ? { maxHeight: "40px" } : {}}
          />
        </CompanyInfo>
        <ReceiptInfo>
          <ReceiptLabel
            style={isPDF ? { color: "black", marginBottom: "0.25rem" } : {}}
          >
            Receipt No
          </ReceiptLabel>
          <ReceiptNumber
            style={isPDF ? { color: "#4f46e5", marginBottom: "0.1rem" } : {}}
          >
            #{order?.unique_slug || order?.unique_id || "N/A"}
          </ReceiptNumber>
          <ReceiptDate
            style={isPDF ? { color: "#333", fontSize: "0.8rem" } : {}}
          >
            Order Date: {formattedDate}
          </ReceiptDate>
        </ReceiptInfo>
      </ReceiptLogoSection>

      <ReceiptSection style={isPDF ? { marginBottom: "1rem" } : {}}>
        <AddressGrid style={isPDF ? { gap: "1rem", marginBottom: "1rem" } : {}}>
          <AddressBlock>
            <AddressTitle
              style={isPDF ? { color: "black", marginBottom: "0.25rem" } : {}}
            >
              From:
            </AddressTitle>
            <AddressContent
              style={isPDF ? { color: "#333", fontSize: "0.85rem" } : {}}
            >
              Liferx MD Inc
              <br />
              (609) 201-0119
              <br />
              info@liferxtelemd.com
              <br />
              401 Cooper Landing Rd # C1
              <br />
              Cherry Hill, NJ 08002
            </AddressContent>
          </AddressBlock>
          <AddressBlock>
            <AddressTitle
              style={isPDF ? { color: "black", marginBottom: "0.25rem" } : {}}
            >
              To:
            </AddressTitle>
            <AddressContent
              style={isPDF ? { color: "#333", fontSize: "0.85rem" } : {}}
            >
              <CustomerValue style={isPDF ? { marginBottom: "0.25rem" } : {}}>
                {order.patient_name || "N/A"}
              </CustomerValue>

              <CustomerValue style={isPDF ? { marginBottom: "0.25rem" } : {}}>
                {order.patient_phone || "N/A"}
              </CustomerValue>

              <CustomerValue style={isPDF ? { marginBottom: "0.25rem" } : {}}>
                {order.patient_email || "N/A"}
              </CustomerValue>

              <CustomerValue>
                {order.shipping_address ? (
                  <>
                    {order.shipping_address.address1}
                    {order.shipping_address.address2 && (
                      <>
                        <br />
                        {order.shipping_address.address2}
                      </>
                    )}
                    <br />
                    {order.shipping_address.city},{" "}
                    {order.shipping_address.state}{" "}
                    {order.shipping_address.zipcode}
                  </>
                ) : (
                  "N/A"
                )}
              </CustomerValue>
            </AddressContent>
          </AddressBlock>
        </AddressGrid>
      </ReceiptSection>

      <ReceiptSection style={isPDF ? { marginBottom: "1rem" } : {}}>
        <SectionTitle
          style={
            isPDF
              ? {
                  color: "black",
                  borderBottom: "1px solid #ddd",
                  marginBottom: "0.75rem",
                  paddingBottom: "0.25rem",
                  fontSize: "1rem"
                }
              : {}
          }
        >
          Order Details
        </SectionTitle>
        <Table>
          <TableHead style={isPDF ? { backgroundColor: "#f5f5f5" } : {}}>
            <tr>
              <th
                style={
                  isPDF ? { color: "black", padding: "0.5rem 0.75rem" } : {}
                }
              >
                Item
              </th>
              <th
                style={{
                  ...(isPDF
                    ? { color: "black", padding: "0.5rem 0.75rem" }
                    : {}),
                  textAlign: "center"
                }}
              >
                Quantity
              </th>
              <th
                style={{
                  ...(isPDF
                    ? { color: "black", padding: "0.5rem 0.75rem" }
                    : {}),
                  textAlign: "right"
                }}
              >
                Price
              </th>
            </tr>
          </TableHead>
          <TableBody>
            {order.order_items &&
              order.order_items.map((item, index) => (
                <tr key={index}>
                  <td style={isPDF ? { padding: "0.5rem 0.75rem" } : {}}>
                    <ItemName
                      style={
                        isPDF ? { color: "black", fontSize: "0.9rem" } : {}
                      }
                    >
                      {item.medicine?.medicine_name || "Unknown Medicine"}
                    </ItemName>
                    <ItemDescription
                      style={isPDF ? { color: "#333", fontSize: "0.8rem" } : {}}
                    >
                      {item.variant
                        ? `${item.variant.quantity} ${item.variant.unit}`
                        : "Standard Package"}
                      {item.variant?.note && ` - ${item.variant.note}`}
                    </ItemDescription>
                  </td>
                  <td
                    style={{
                      textAlign: "center",
                      ...(isPDF
                        ? { color: "#333", padding: "0.5rem 0.75rem" }
                        : {})
                    }}
                  >
                    1
                  </td>
                  <td
                    style={{
                      textAlign: "right",
                      ...(isPDF
                        ? { color: "#333", padding: "0.5rem 0.75rem" }
                        : {})
                    }}
                  >
                    {formatCurrency(item.price || 0)}
                  </td>
                </tr>
              ))}
          </TableBody>
        </Table>

        <TotalSection
          style={
            isPDF ? { borderTop: "1px solid #ddd", paddingTop: "0.75rem" } : {}
          }
        >
          <TotalRow style={isPDF ? { marginBottom: "0.3rem" } : {}}>
            <TotalLabel style={isPDF ? { color: "#333" } : {}}>
              Subtotal:
            </TotalLabel>
            <TotalValue style={isPDF ? { color: "black" } : {}}>
              {formatCurrency(subtotal)}
            </TotalValue>
          </TotalRow>
          <TotalRow style={isPDF ? { marginBottom: "0.3rem" } : {}}>
            <TotalLabel style={isPDF ? { color: "#333" } : {}}>
              Shipping Fee:
            </TotalLabel>
            <TotalValue style={isPDF ? { color: "black" } : {}}>
              {formatCurrency(order.shipping_fee || 0)}
            </TotalValue>
          </TotalRow>
          <TotalRow style={isPDF ? { marginBottom: "0.3rem" } : {}}>
            <TotalLabel style={isPDF ? { color: "#333" } : {}}>
              Discount:
            </TotalLabel>
            <TotalValue style={isPDF ? { color: "black" } : {}}>
              -{formatCurrency(order.discount || 0)}
            </TotalValue>
          </TotalRow>
          <TotalRow style={isPDF ? { paddingTop: "0.3rem" } : {}}>
            <TotalLabel style={isPDF ? { color: "#333" } : {}}>
              Total:
            </TotalLabel>
            <GrandTotal style={isPDF ? { color: "#4f46e5" } : {}}>
              {formatCurrency(order.total_fee || 0)}
            </GrandTotal>
          </TotalRow>
        </TotalSection>
      </ReceiptSection>



      <Footer
        style={
          isPDF
            ? {
                borderTop: "1px solid #ddd",
                color: "#555",
                marginTop: "1rem",
                paddingTop: "0.75rem"
              }
            : {}
        }
      >
        <ThankYou
          style={
            isPDF
              ? { color: "black", fontSize: "1rem", marginBottom: "0.3rem" }
              : {}
          }
        >
          Thank you for your order!
        </ThankYou>
        <p style={isPDF ? { fontSize: "0.8rem", margin: "0" } : {}}>
          For any questions or concerns, please contact our customer support
          (609) 201-0119.
        </p>
      </Footer>
    </ReceiptContent>
  );

  return (
    <PageWrapper>
      <ReceiptContainer>
        <ReceiptHeader>
          <ReceiptTitle>Receipt</ReceiptTitle>
          <BackButton to={`/orders`}>
            <FaArrowLeft />
            Back to Order
          </BackButton>
        </ReceiptHeader>

        <Card>
          {/* Visible receipt */}
          <div ref={receiptRef}>{renderReceiptContent(false)}</div>

          {/* Hidden receipt for PDF generation */}
          <PDFReceiptContainer ref={pdfReceiptRef}>
            {renderReceiptContent(true)}
          </PDFReceiptContainer>

          <Actions>
            <Button primary onClick={handlePrint} disabled={isPrinting}>
              {isPrinting ? <FaSpinner /> : <FaPrint />}
              {isPrinting ? "Printing..." : "Print Receipt"}
            </Button>
            <Button
              onClick={handleDownloadPDF}
              disabled={isGeneratingPDF}
              primary
            >
              {isGeneratingPDF ? <FaSpinner /> : <FaDownload />}
              {isGeneratingPDF ? "Generating PDF..." : "Download PDF"}
            </Button>
          </Actions>
        </Card>
      </ReceiptContainer>
    </PageWrapper>
  );
};

export default ReceiptPage;
